import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Lock, ArrowLeft } from "lucide-react";
import { EpisodeCard } from "../components/Cards";

export default function EpisodeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ep, setEp] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/episodes/${id}`)
      .then((r) => setEp(r.data))
      .catch(() => setEp(null))
      .finally(() => setLoading(false));
    api.get("/episodes", { params: { limit: 4 } }).then((r) => setRelated(r.data.filter((x) => x.id !== id).slice(0, 3)));
  }, [id]);

  if (loading) {
    return <div className="pt-40 text-center" style={{ color: "var(--ink-muted)" }}>Loading…</div>;
  }
  if (!ep) {
    return <div className="pt-40 text-center" style={{ color: "var(--ink-muted)" }}>Episode not found.</div>;
  }

  return (
    <div data-testid="episode-detail" className="pt-24">
      {/* PLAYER / COVER */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8">
        <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-[0.22em] mb-6 flex items-center gap-2 hover:text-[var(--brass)] transition-colors" style={{ color: "var(--ink-muted)" }}>
          <ArrowLeft size={14} /> Back
        </button>

        <div className="relative aspect-video overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--hairline)" }}>
          {ep.locked ? (
            <>
              <img src={ep.cover_image} alt={ep.title} className="w-full h-full object-cover" style={{ filter: "brightness(0.5)" }} />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <Lock size={36} style={{ color: "var(--brass)" }} />
                <p className="eyebrow mt-4 mb-2">Members Only · {ep.tier?.toUpperCase()}</p>
                <h3 className="font-display text-3xl md:text-4xl mb-6 max-w-md">This interview is reserved for subscribers.</h3>
                <Link to="/subscribe" data-testid="locked-subscribe-cta" className="btn-brass">Become a Member</Link>
              </div>
            </>
          ) : (
            <video
              data-testid="episode-video"
              src={ep.video_url}
              poster={ep.cover_image}
              controls
              playsInline
              className="w-full h-full object-cover"
            />
          )}
        </div>
      </section>

      {/* META + SYNOPSIS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <p className="eyebrow mb-4">{ep.industry} · {ep.runtime} · Tier {ep.tier?.toUpperCase()}</p>
            <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">{ep.title}</h1>
            <p className="text-lg leading-relaxed mb-8 drop-cap" style={{ color: "var(--ink)" }}>
              {ep.synopsis}
            </p>
          </div>
          <aside className="lg:col-span-4 lg:pl-8" style={{ borderLeft: "1px solid var(--hairline)" }}>
            <p className="eyebrow mb-3">The Guest</p>
            <h3 className="font-display text-3xl mb-1">{ep.guest_name}</h3>
            <p className="text-sm" style={{ color: "var(--ink-muted)" }}>{ep.guest_title}</p>
            <hr className="hairline my-6" />
            <p className="eyebrow mb-3">Hosted By</p>
            <p className="text-base" style={{ color: "var(--ink)" }}>{ep.host}</p>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <>
          <hr className="hairline max-w-[1400px] mx-auto" />
          <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
            <p className="eyebrow mb-3">Continue</p>
            <h2 className="font-display text-4xl mb-10">More from the Library</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {related.map((r) => <EpisodeCard key={r.id} ep={r} />)}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
